import {
  isRealEstateSectorAllowedList,
  normalizeToRealEstateSectorAllowedValue,
} from './soteriaRealEstateSector';

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Maps a raw extracted string to an exact Soteria pick-list value when possible.
 * Mirrors backend soteriaPickListNormalize.ts.
 */
export function normalizeToAllowedValue(
  raw: string,
  allowedValues: readonly string[]
): string | null {
  const trimmed = raw.trim();
  if (!trimmed || allowedValues.length === 0) return null;

  if (allowedValues.includes(trimmed)) return trimmed;

  const lower = trimmed.toLowerCase();
  const caseInsensitive = allowedValues.filter((v) => v.toLowerCase() === lower);
  if (caseInsensitive.length === 1) return caseInsensitive[0]!;
  if (caseInsensitive.length > 1) return caseInsensitive[0]!;

  const partMatches = allowedValues.filter((allowed) => {
    const parts = allowed
      .split(/[/,;]/)
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);
    return parts.some((part) => part === lower);
  });
  if (partMatches.length === 1) return partMatches[0]!;
  if (partMatches.length > 1) {
    return partMatches.sort((a, b) => a.length - b.length)[0]!;
  }

  const wordMatches = allowedValues.filter((allowed) => {
    const re = new RegExp(`\\b${escapeRegex(lower)}\\b`, 'i');
    return re.test(allowed);
  });
  if (wordMatches.length === 1) return wordMatches[0]!;

  const containsMatches = allowedValues.filter((allowed) => {
    const aLower = allowed.toLowerCase();
    return aLower.includes(lower) || lower.includes(aLower);
  });
  if (containsMatches.length === 1) return containsMatches[0]!;

  return null;
}

/**
 * Resolves a raw value to an allowed pick-list entry, including domain-specific
 * mappings (e.g. Sector: Multifamily → Residential).
 */
export function normalizePickListValue(
  raw: string,
  allowedValues: readonly string[],
  fieldName?: string
): string | null {
  if (fieldName?.trim() === 'Sector' || isRealEstateSectorAllowedList(allowedValues)) {
    return normalizeToRealEstateSectorAllowedValue(raw, allowedValues);
  }
  return normalizeToAllowedValue(raw, allowedValues);
}
